// Modules
import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";

// Selfie Segmentation
var selfieSegmentation: any;

/**
 * Dish Background Video
 * @description Class to add a background to a video
 * @param {MediaStream} stream
 * @param {HTMLImageElement} background
 */
export class DishBackgroundVideo {
  stream: MediaStream;
  background: HTMLImageElement;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  video: HTMLVideoElement;
  selfieSegmentation: SelfieSegmentation;
  processing: boolean = false;
  isActive: boolean = true;
  processingResolution: { width: number, height: number };
  outputResolution: { width: number, height: number };
  frameRate: number = 15; // Default to 15 FPS instead of 24
  lastPerformanceCheck: number = 0;
  performanceHistory: number[] = [];
  modelLoaded: boolean = false;
  lastFrameTime: number = 0;
  isClosed: boolean = false;
  animationFrameId: number | null = null;
  isInitialized: boolean = false;
  isRunning: any;
  intervalId: number | null = null;

  constructor({
    stream,
    background,
  }: {
    stream: MediaStream;
    background: HTMLImageElement;
  }) {
    // save stream and background
    this.stream = stream;
    this.background = background;

    let width = stream.getVideoTracks()[0].getSettings().width || 1280;
    let height = stream.getVideoTracks()[0].getSettings().height || 720;

    if (width > 1280) width = 1280;
    if (height > 720) height = 720;

    // Store original resolution as output resolution
    this.outputResolution = { width, height };

    // Use lower resolution for processing on weaker devices
    // We'll dynamically adjust this based on performance
    this.processingResolution = {
      width: Math.min(640, width),
      height: Math.min(480, height)
    };

    // create canvas
    this.canvas = document.createElement("canvas");
    this.canvas.width = width;
    this.canvas.height = height;

    // get context
    const context = this.canvas.getContext("2d");
    if (!context) throw new Error("Canvas context not found");
    this.context = context;

    // Improve quality of the image but use medium for better performance
    this.context.imageSmoothingEnabled = true;
    this.context.imageSmoothingQuality = "medium";

    // create video
    this.video = document.createElement("video");
    this.video.width = this.processingResolution.width;
    this.video.height = this.processingResolution.height;
    this.video.muted = true;
    this.video.srcObject = stream;
    this.video.autoplay = true;

    // Importante: Evitamos que el navegador suspenda el video cuando está en background
    this.video.playsInline = true;
    this.video.setAttribute("disablePictureInPicture", "true");

    // add video to body
    document.body.appendChild(this.video);

    // set video style
    this.video.style.position = "fixed";
    this.video.style.right = "0";
    this.video.style.top = "0";
    this.video.style.width = `${this.processingResolution.width}px`;
    this.video.style.height = `${this.processingResolution.height}px`;
    this.video.style.zIndex = "-9999";
    this.video.style.opacity = "0";
    this.video.style.pointerEvents = "none";

    // selfie segmentation
    selfieSegmentation = this.selfieSegmentation = new SelfieSegmentation({
      locateFile: (file: any) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
    });

    // set options
    this.selfieSegmentation.setOptions({
      modelSelection: 1,
    });

    // Track visibility changes to pause processing when tab is hidden
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // detect model loaded
    this.selfieSegmentation.onResults((results: any) => {
      if (!this.isClosed) {
        if (!this.modelLoaded) {
          this.modelLoaded = true;
          console.log("Background segmentation model loaded");
        }
        this.onResults(results);
        this.processing = false;
      }
    });
  }

  // Handle visibility changes as a bound method
  handleVisibilityChange() {
    const isVisible = document.visibilityState === "visible";

    // Evitamos cambios repetidos en el mismo estado
    if (isVisible === this.isActive) return;

    this.isActive = isVisible;

    if (isVisible) {
      // Si vuelve a ser visible, detener intervalo y usar animationFrame
      this.stopInterval();
      this.isRunning = true;
      this.startAnimationFrame();
      console.log("Pestaña visible: usando animationFrame");
    } else {
      // Si pasa a segundo plano, detener animationFrame y usar intervalo
      this.stopAnimationFrame();
      this.isRunning = true;
      this.startInterval();
      console.log("Pestaña en segundo plano: usando interval");
    }
  }

  onResults(results: any) {
    // clear canvas
    this.context.drawImage(
      results.segmentationMask,
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );

    // Only overwrite existing pixels.
    this.context.globalCompositeOperation = "source-out";

    // draw background
    if (this.background.width) {
      const canvasAspect = this.canvas.width / this.canvas.height;
      const imageAspect = this.background.width / this.background.height;

      let renderHeight = this.canvas.height;
      let renderWidth = this.canvas.width;
      let x = 0;
      let y = 0;

      if (canvasAspect > imageAspect) {
        renderHeight = this.canvas.width / imageAspect;
        y = (this.canvas.height - renderHeight) / 2;
      } else {
        renderWidth = this.canvas.height * imageAspect;
        x = (this.canvas.width - renderWidth) / 2;
      }

      this.context.drawImage(
        this.background,
        0,
        0,
        this.background.width,
        this.background.height,
        x,
        y,
        renderWidth,
        renderHeight
      );
    }

    // Only overwrite missing pixels.
    this.context.globalCompositeOperation = "destination-atop";

    // draw image
    this.context.drawImage(
      results.image,
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );

    // Only overwrite missing pixels.
    this.context.restore();
  }

  private isSelfieSegmentationClosed = false;

  async processFrame() {
    if (!this.video.width || this.isSelfieSegmentationClosed) return;

    try {
      await this.selfieSegmentation.send({ image: this.video });
    } catch (error) {
      console.log("Error processing frame, selfie segmentation may be closed:", error);
      // Si ocurre un error, podemos detener el procesamiento para evitar errores en cascada
      this.isRunning = false;
    }
  }

  startAnimationFrame() {
    const FPS = 24;
    const frameInterval = 1000 / FPS; // Time between frames in ms
    let lastFrameTime = 0;
    let animationFrameId: number;

    const animateFrame = async (timestamp: number) => {
      if (!this.isRunning) return;

      // Calculate time since last frame
      const elapsed = timestamp - lastFrameTime;

      // Skip frame if not enough time has passed
      if (elapsed >= frameInterval) {
        // Process frame and update timestamp
        lastFrameTime = timestamp;
        await this.processFrame();
      }

      animationFrameId = requestAnimationFrame(animateFrame);
    };

    animationFrameId = requestAnimationFrame(animateFrame);
  }

  startInterval() {
    // Usar un intervalo más lento cuando está en background para ahorrar recursos
    const backgroundFPS = 10;
    this.intervalId = window.setInterval(async () => {
      await this.processFrame();
    }, 1000 / backgroundFPS) as unknown as number;
  }

  stopAnimationFrame() {
    // Nada que detener específicamente ya que requestAnimationFrame se manejará
    // por la condición this.isRunning en animateFrame
  }

  stopInterval() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  start() {
    if (this.isClosed) {
      console.warn("Cannot start a closed DishBackgroundVideo instance");
      return;
    }

    this.isInitialized = true;
    console.log(
      `Processing resolution: ${this.processingResolution.width}x${this.processingResolution.height}, Output: ${this.outputResolution.width}x${this.outputResolution.height}`
    );

    // Start with conservative frame rate
    this.frameRate = this.detectInitialFrameRate();
    console.log(`Initial frame rate: ${this.frameRate} FPS`);

    const processFrame = async (timestamp: number) => {
      // Don't continue the animation loop if instance has been closed
      if (this.isClosed) return;

      if (!this.video.width) {
        this.animationFrameId = requestAnimationFrame(processFrame);
        return;
      }

      // Skip processing if tab is not visible or already processing
      if (!this.isActive || this.processing) {
        this.animationFrameId = requestAnimationFrame(processFrame);
        return;
      }

      // Adjust frame rate based on performance every 5 seconds
      if (timestamp - this.lastPerformanceCheck > 5000) {
        this.adjustPerformance();
        this.lastPerformanceCheck = timestamp;
      }

      // Calculate time since last frame
      const frameInterval = 1000 / this.frameRate;
      const elapsed = timestamp - (this.lastFrameTime || 0);

      // Skip frame if not enough time has passed
      if (elapsed < frameInterval) {
        this.animationFrameId = requestAnimationFrame(processFrame);
        return;
      }

      // Process frame and update timestamp
      this.processing = true;
      this.lastFrameTime = timestamp;

      try {
        const startTime = performance.now();
        // Only send to selfieSegmentation if not closed
        if (!this.isClosed && this.selfieSegmentation) {
          await this.selfieSegmentation.send({ image: this.video });

          // Track performance
          const frameTime = performance.now() - startTime;
          this.performanceHistory.push(frameTime);
          if (this.performanceHistory.length > 30) this.performanceHistory.shift();
        }
      } catch (error) {
        console.error("Error processing frame:", error);
        this.processing = false;
      }

      if (!this.isClosed) {
        this.animationFrameId = requestAnimationFrame(processFrame);
      }
    };

    this.animationFrameId = requestAnimationFrame(processFrame);
  }

  // Detect device capabilities and set initial frame rate
  detectInitialFrameRate(): number {
    // Simple heuristic based on resolution and device memory
    // @ts-ignore - navigator.deviceMemory is not in TypeScript defs yet
    const memory = navigator.deviceMemory || 4; // Default to 4GB if not available

    if (memory <= 2) return 10; // Low-end devices: 10 FPS
    if (memory <= 4) return 15; // Mid-range devices: 15 FPS
    return 20; // High-end devices: 20 FPS (reduced from 24)
  }

  // Adjust performance settings based on historical performance
  adjustPerformance() {
    if (this.performanceHistory.length < 10) return;

    // Calculate average frame processing time
    const avgFrameTime = this.performanceHistory.reduce((a, b) => a + b, 0) / this.performanceHistory.length;
    const targetFrameTime = 1000 / this.frameRate * 0.8; // Target 80% of frame budget

    console.log(`Avg frame time: ${avgFrameTime.toFixed(1)}ms, Target: ${targetFrameTime.toFixed(1)}ms`);

    // If processing is too slow, reduce quality
    if (avgFrameTime > targetFrameTime * 1.2) {
      // First try reducing frame rate
      if (this.frameRate > 10) {
        this.frameRate = Math.max(10, this.frameRate - 2);
        console.log(`Reducing frame rate to ${this.frameRate} FPS`);
      }
      // Then try reducing resolution
      else if (this.processingResolution.width > 320) {
        this.processingResolution.width = Math.max(320, this.processingResolution.width * 0.8);
        this.processingResolution.height = Math.max(240, this.processingResolution.height * 0.8);

        this.video.width = this.processingResolution.width;
        this.video.height = this.processingResolution.height;
        this.video.style.width = `${this.processingResolution.width}px`;
        this.video.style.height = `${this.processingResolution.height}px`;

        console.log(`Reducing processing resolution to ${this.processingResolution.width}x${this.processingResolution.height}`);
      }

      // Clear history after adjustment
      this.performanceHistory = [];
    }

    // If processing is very fast, we can increase quality
    else if (avgFrameTime < targetFrameTime * 0.5 && this.frameRate < 20) {
      this.frameRate = Math.min(20, this.frameRate + 2);
      console.log(`Increasing frame rate to ${this.frameRate} FPS`);
      this.performanceHistory = [];
    }
  }

  stop() {
    // Prevent multiple stop calls
    if (this.isClosed) {
      console.log("Background segmentation already closed");
      return;
    }

    console.log("Stopping background segmentation");
    this.isClosed = true;

    // Cancel animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Remove event listeners
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);

    try {
      // Only close if it exists and hasn't been closed
      if (this.selfieSegmentation) {
        this.selfieSegmentation.close();
        selfieSegmentation = null;
      }

      // Remove global instance reference only if it matches our instance
      if (selfieSegmentation === this.selfieSegmentation) {
        selfieSegmentation = null;
      }
    } catch (error) {
      console.error("Error while closing selfie segmentation:", error);
    }

    // Clean up video element
    if (this.video && this.video.parentNode) {
      this.video.srcObject = null;
      this.video.parentNode.removeChild(this.video);
    }

    console.log("Background segmentation stopped");
  }

  run() {
    // start
    if (this.isClosed) {
      console.warn("Cannot run a closed DishBackgroundVideo instance");
      return;
    }

    setTimeout(async () => {
      if (!this.isClosed) {
        this.start();
      }
    }, 1000);
  }

  async getStream() {
    if (this.isClosed) {
      throw new Error("Cannot get stream from closed DishBackgroundVideo instance");
    }

    // get stream with adaptive frame rate
    const streamResult = this.canvas.captureStream(this.frameRate);

    // add audio
    const [audioTrack] = this.stream.getAudioTracks();

    // add audio track
    if (audioTrack) streamResult.addTrack(audioTrack);

    // return stream
    return streamResult;
  }
}
